import { theme } from 'antd';
import React from 'react';
import styled from 'styled-components';

// This is our own copy of the Comment layout antd removed in v5, with
// the style modifications made by AdHub folded in and unused features
// dropped. According to ant-design it was dropped because it was just a
// prefab layout, with no functionality, state, etc. It does live on in
// the package `@ant-design/compatible`, but it is not clear how support
// for that will look when upgrading to antd v6. Hence reproducing our
// own copy.

// Tokens at module scope: see components/UI/Table/Column.tsx for the
// rationale.
//
// Note that we deliberately inherit the fontFamily, - that is an
// intentional deviation from the antd v4 and the 4-to-5-compat version.
const {
  colorBgContainer,
  colorText,
  colorTextSecondary,
  colorTextTertiary,
  controlHeight,
  fontSize,
  fontSizeSM,
  lineHeight,
  lineHeightSM,
  marginSM,
  marginXXS,
  motionDurationSlow,
  paddingXS,
} = theme.getDesignToken();

const Root = styled.div`
  position: relative;
  width: 100%;
  margin: 0;
  padding: 0;
  background: ${colorBgContainer};
  color: ${colorText};
  font-size: ${fontSize}px;
  line-height: ${lineHeight};
`;

const Inner = styled.div`
  display: flex;
  padding: 0;
`;

// Both avatars the app passes already size themselves, so the <img>
// sizing only matters for a future caller handing over a bare <img>.
const Avatar = styled.div`
  position: relative;
  flex-shrink: 0;
  margin-inline-end: ${marginSM}px;

  img {
    width: ${controlHeight}px;
    height: ${controlHeight}px;
    border-radius: 50%;
  }
`;

const Content = styled.div`
  position: relative;
  flex: auto;
  min-width: 0;
  overflow-wrap: break-word;
`;

const AuthorRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-start;
  margin-bottom: ${marginXXS}px;

  > span {
    padding-inline-end: ${paddingXS}px;
    font-size: ${fontSizeSM}px;
    line-height: ${lineHeightSM};
  }
`;

const AuthorName = styled.span`
  color: ${colorTextSecondary};
  transition: color ${motionDurationSlow};
`;

const AuthorTime = styled.span`
  color: ${colorTextTertiary};
  white-space: nowrap;
  cursor: auto;
`;

// `white-space: pre-wrap` is folded in from the call sites. antd only
// ever styled `-detail p`, and the content passed here is a bare string
// (or a textarea while editing), never a <p> -- which is why the call
// sites had to add this themselves.
const Detail = styled.div`
  white-space: pre-wrap;
`;

// `margin: 3px 0` is folded in from the call sites; antd had
// `margin-top: 12px; margin-bottom: 0`. `list-style: none` is explicit
// here because antd got it by inheritance from resetComponent() on the
// root, which this component doesn't reproduce.
const Actions = styled.ul`
  margin: 3px 0;
  padding-inline-start: 0;
  list-style: none;

  > li {
    display: inline-block;
    color: ${colorTextSecondary};

    > span {
      margin-inline-end: ${marginSM}px;
      color: ${colorTextSecondary};
      font-size: ${fontSizeSM}px;
      cursor: pointer;
      transition: color ${motionDurationSlow};
      user-select: none;

      &:hover {
        color: ${colorText};
      }
    }
  }
`;

export interface CommentLayoutProps {
  actions?: React.ReactNode[];
  author?: React.ReactNode;
  avatar?: React.ReactNode;
  className?: string;
  content?: React.ReactNode;
  datetime?: React.ReactNode;
}

function CommentLayout({
  actions,
  author,
  avatar,
  className,
  content,
  datetime,
}: CommentLayoutProps) {
  return (
    <Root className={className}>
      <Inner>
        {avatar && <Avatar>{avatar}</Avatar>}

        <Content>
          {(author || datetime) && (
            <AuthorRow>
              {author && <AuthorName>{author}</AuthorName>}
              {datetime && <AuthorTime>{datetime}</AuthorTime>}
            </AuthorRow>
          )}

          <Detail>{content}</Detail>

          {actions && actions.length > 0 && (
            <Actions>
              {actions.map((action, index) => (
                <li key={`action-${index}`}>{action}</li>
              ))}
            </Actions>
          )}
        </Content>
      </Inner>
    </Root>
  );
}

export default CommentLayout;
